import React, { useMemo } from "react";

import Leaders from "./Leaders";
import Podium from "../../../pages/CompanyView/Podium";
import ProgressBar from "./ProgressBar";
import SoftBox from "../../SoftBox";
import SoftButton from "../../SoftButton";
import { Grid, useMediaQuery } from '@mui/material';
import { ptsPerAmtOf, recomputeScopedPlayerScores } from "../../../helpers/kpiPoints";
import { recomputeClosedMonth } from "../../../helpers/historicMonthBoard";

/**
 * The dashboard's podium block — the Company View 3D podium (laurel frames +
 * brand cylinders) fed with the top three players, scaled to fit whatever
 * width the admin gives the block.
 */
const DashboardPodium = ({ top3 }) => {
  const ref = React.useRef(null);
  const [box, setBox] = React.useState({ w: 0, h: 0 });
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      if (entries[0]) setBox({ w: entries[0].contentRect.width, h: entries[0].contentRect.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const w = box.w || 400;
  const h = box.h || 520;
  // Width: the three columns span ~496px at scale 1 (200k + 2×172k − 2×24k
  // overlap) — stretch to ~97% of the card, capped so laurels/avatars never
  // balloon on wide stacked layouts. NO lower floor: the old 0.6 minimum
  // meant any card narrower than ~330px physically couldn't hold the podium
  // and overflow:hidden clipped the 3rd column (the laptop-width QA bug).
  // Text inside colHTML() clamps its own minimum font sizes, so small scales
  // stay legible.
  const scale = Math.min(1.1, (w * 0.97) / 496);
  // Height: grow the pedestals so the podium stands ~92% of the card tall.
  // Rendered 1st-place cylinder ≈ colW * (bh + 64) / 150 with colW = 200·k.
  const headZone = 134 * scale + 38; // laurel frame + name + points
  const targetCyl = h * 0.92 - headZone;
  let pedestal = ((targetCyl * 150) / (200 * scale) - 64) / 96;
  // Cap was 2.8 — on tall cards that stranded the podium in the bottom half
  // with dead air above (QA). 6 lets the columns actually reach ~92% height;
  // the floor keeps short cards from inverting.
  pedestal = Math.max(1, Math.min(6, pedestal));
  const data = top3.map((u) => ({
    name: u.firstName ? `${u.firstName} ${u.lastName || ""}`.trim() : "Player",
    points: Math.round(u.scores?.total || 0),
    image: u.image,
  }));
  return (
    <div ref={ref} style={{ width: "100%", height: "100%", display: "flex", alignItems: "flex-end", justifyContent: "center", overflow: "hidden" }}>
      <Podium top3={data} scale={scale} pedestal={pedestal} />
    </div>
  );
};

const LiveLeaderboardComponent = ({
  topList = [],
  groupKpis = null,
  allGroupKpis = null,
  allKpis = null,
  isAdmin = false,
  isViewOnly = false,
  isPointsPercent = false,
  // Company View passes normalized performance totals rather than raw points.
  usePrecomputedTotal = false,
  // Company View needs all rows in a fixed-height scroller, not pagination.
  scrollable = false,
  periodIdx = 0,
  // "full" = ranked list + podium side by side (the classic board).
  // "list" / "podium" render just that half, full width — used by the
  // customizable dashboard where each half is its own block.
  section = "full",
  // 'default' = sum across all workspace KPIs. Any other value = a single
  // KPI id selected from the "Filter by KPI" dropdown, in which case the
  // total should only count that KPI's points.
  kpiId = "default",
}) => {
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const [displayMode, setDisplayMode] = React.useState('initial'); // 'initial', 'more', 'all'
  // Bigger pager tap target on mobile only (28px was under the ~32-36px
  // comfortable-touch guideline) — desktop keeps the original 28px.
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const handleToggleDisplay = () => {
    setDisplayMode(prevMode => {
      if (prevMode === 'initial') return 'more';
      if (prevMode === 'more') return 'all';
      return 'initial';
    });
  };

  // Recompute rows with the same canonical helper used by the rest of the
  // dashboard. This preserves correct anchor handling and avoids the old
  // reciprocal point-scale calculation.
  const isSingleKpi = kpiId && kpiId !== "default";
  // Last Month is a closed month: score against the SAME workspace KPI set the
  // live board uses (allGroupKpis), just recomputing anchors progressively so
  // closed-month anchor metrics resolve. Using the workspace set (not a
  // cross-workspace "any KPI with a score" superset) keeps Last Month totals
  // consistent with This Month for the same workspace.
  const isClosedMonth = periodIdx === 5;
  const scoringKpis = Array.isArray(allGroupKpis) ? allGroupKpis : groupKpis;
  const scopedList = usePrecomputedTotal
    ? topList
    : topList.map((user) => {
        /* Yeti totals come from buildYetiScoresForKpis (Today/Yesterday already
           exclude anchor benchmarks). Recompute would rebuild `.total` from
           per-KPI pts and put the ~2.7k inflated day total back on the board
           while KOTM (which uses yeti.total directly) looked fixed. */
        if (user.isYeti) return user;
        let kpiListForScope = scoringKpis;
        if (!Array.isArray(scoringKpis) && allKpis && allKpis[user.userId]) {
          kpiListForScope = allKpis[user.userId];
        }
        if (isClosedMonth && !isSingleKpi) {
          return {
            ...user,
            scores: recomputeClosedMonth(user.scores, kpiListForScope),
          };
        }
        const filteredScores = recomputeScopedPlayerScores(user.scores, kpiListForScope, {
          singleKpiId: isSingleKpi ? kpiId : null,
          ...(isClosedMonth && isSingleKpi ? { recomputeAnchorAsProgressive: true } : {}),
        });
        return { ...user, scores: filteredScores };
      });

  // Sort the scoped list — active users by score, inactive users at bottom.
  const sortedList = [...scopedList].sort((a, b) => {
    if (a.isActive === false && b.isActive !== false) return 1;
    if (a.isActive !== false && b.isActive === false) return -1;
    return b.scores?.total - a.scores?.total;
  });

  const versusYetiScores = useMemo(
    () => sortedList.find((u) => u.isYeti)?.scores || null,
    [sortedList],
  );

  const visibleList = useMemo(
    () => sortedList.filter((u) => u.isYeti
      || (u.isArchived !== true && u.isActive !== false)),
    [sortedList],
  );

  const getDisplayedList = () => {
    switch (displayMode) {
      case 'initial': return visibleList.slice(0, 5);
      case 'more': return visibleList.slice(0, 10);
      case 'all': return visibleList;
      default: return visibleList.slice(0, 5);
    }
  };

  // `filteredDisplayedList` now mirrors `getDisplayedList()` since totals are
  // already scoped at the top. Kept the name for callsite parity.
  const filteredDisplayedList = scrollable ? visibleList : getDisplayedList();

  // Dashboard list block: pages of ten with arrows instead of Show More.
  const PAGE_SIZE = 10;
  const [page, setPage] = React.useState(0);
  const pageCount = Math.max(1, Math.ceil(visibleList.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  React.useEffect(() => { setPage(0); }, [topList.length, kpiId, periodIdx]);
  const pagedList = visibleList.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

  const rowsToRender = section === "list" ? pagedList : filteredDisplayedList;
  const rankOffset = section === "list" ? currentPage * PAGE_SIZE : 0;

  const pagerArrow = (dir, disabled, onClick) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={dir === "prev" ? "Previous page" : "Next page"}
      style={{
        width: isMobile ? 36 : 28,
        height: isMobile ? 36 : 28,
        borderRadius: 8,
        border: "1px solid var(--tvd-hair, #EAECF0)",
        background: "var(--tvd-tile, #fff)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.35 : 1,
      }}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--tvd-body, #475467)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: dir === "prev" ? "rotate(180deg)" : "none" }}>
        <path d="M9 6l6 6-6 6" />
      </svg>
    </button>
  );

  const getButtonText = () => {
    if (displayMode === 'all') return 'Hide';
    return 'Show More';
  };

  let rootSx = { marginTop: { mini: "1rem", lg: "2rem" } };
  if (section === "podium") rootSx = { marginTop: 0, height: "100%" };
  else if (section === "list") rootSx = { marginTop: "14px" };

  // The pager is pinned to the card's bottom-right so it never jumps when a
  // page holds fewer rows — which means the rows have to leave room for it,
  // or the last row's points end up sitting underneath it.
  const showPager = section === "list" && visibleList.length > PAGE_SIZE;

  return (
    <SoftBox sx={rootSx}>
      <Grid container sx={section === "podium" ? { height: "100%" } : undefined}>
        {section !== "podium" && (
        <Grid
          item
          mini={12} md={12} lg={section === "list" ? 12 : 6}
        >
          {topList && topList.length
            ? rowsToRender.map((user, index) => (
                <ProgressBar
                  key={user.userId || user.firstName}
                  userId={user.userId}
                  userFirstName={user.firstName}
                  userLastName={user.lastName}
                  // Use given workspace KPIs or a sub-set from all company KPIs
                  groupKpis={groupKpis ?? allKpis[user.userId]}
                  userKpis={user.scores}
                  isOwnId={user.ownId}
                  imgsrc={user.image}
                  top={visibleList[0]?.scores?.total || 0}
                  email={user.email}
                  position={rankOffset + index + 1}
                  prevPosition={user.yesterdayRank}
                  showRankMove={periodIdx >= 1}
                  isActive={index === activeIndex}
                  onSelect={(show) => setActiveIndex(show ? index : -1)}
                  isYeti={user.isYeti}
                  isViewOnly={isViewOnly}
                  isPointsPercent={isPointsPercent}
                  playerStatus={user.isActive}
                  dense={section === "list"}
                  versusYetiScores={versusYetiScores}
                />
              ))
            : null}
          {section !== "list" && topList.length > 5 && (
            <SoftBox sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <SoftButton
                color="primary"
                sx={{ borderRadius: "8px", fontSize: "12px", lineHeight: "12px", textTransform: "capitalize", marginTop: "2rem", width: "10rem" }}
                onClick={handleToggleDisplay}
              >
                {getButtonText()}
              </SoftButton>
            </SoftBox>
          )}
          {showPager && (
            // In normal flow, right-aligned, directly under the last row. It used
            // to be absolutely pinned to the card's bottom-right, which sat on
            // top of the final row's points whenever the rows filled the card.
            <SoftBox sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "8px", mt: "2px", pr: "20px", lineHeight: 1 }}>
              {pagerArrow("prev", currentPage === 0, () => setPage(currentPage - 1))}
              <span style={{ fontFamily: "DM Sans", fontSize: "12px", fontWeight: 500, color: "#667085", fontVariantNumeric: "tabular-nums" }}>
                {currentPage + 1} of {pageCount}
              </span>
              {pagerArrow("next", currentPage >= pageCount - 1, () => setPage(currentPage + 1))}
            </SoftBox>
          )}
        </Grid>
        )}
        {section !== "list" && (
        <Grid
          item
          mini={12} md={12} lg={section === "podium" ? 12 : 6}
          sx={section === "podium" ? { height: "100%" } : undefined}
        >
          {topList && topList.length > 0 && (
            section === "podium" ? (
              <DashboardPodium top3={visibleList.slice(0, 3)} />
            ) : (
              <Leaders
                top3={visibleList.slice(0, 3)}
                groupKpis={groupKpis}
                showRankMove={periodIdx >= 1}
              />
            )
          )}
        </Grid>
        )}
      </Grid>
    </SoftBox>
  );
};

export default LiveLeaderboardComponent;
